import { useState } from "react";
import { useLocalSearchParams, useRouter } from "expo-router";

import { EligibilityYesAndNo } from "@/blocks/EligibilityYesAndNo";
import { VnLoadingSpinner } from "@/components/VnLoadingSpinner";
import { useUpdateHouseholdMutation } from "@/data/mutations/householdsMutations";
import { useListHouseholdMemberRelationshipsByPrimaryMemberQuery } from "@/data/queries/householdMemberRelationshipsQueries";
import { useCurrentHouseholdMemberQuery } from "@/data/queries/householdMembersQueries";
import { useGetHouseholdQuery } from "@/data/queries/householdsQueries";

import { EligibilityAddHouseholdMemberScreen } from "./EligibilityAddHouseholdMemberScreen";

export const EligibilitySupportOtherRelativesScreen = () => {
  const router = useRouter();

  const { supporterFirstName, supporterHouseholdMemberId } =
    useLocalSearchParams<{
      supporterFirstName: string;
      supporterHouseholdMemberId: string;
    }>();

  const { mutate: updateHousehold, isPending: isUpdatingHousehold } =
    useUpdateHouseholdMutation();
  const { data: currentHouseholdMember } = useCurrentHouseholdMemberQuery();
  const { data: household } = useGetHouseholdQuery();

  const hasFinishedEligibilityFlow =
    household?.eligibilityLastStep === "FINISHED";

  // Don't need to list relationships if it's the supporter screen
  const { data: householdMemberRelationships } =
    useListHouseholdMemberRelationshipsByPrimaryMemberQuery({
      isEnabled: !supporterHouseholdMemberId,
    });

  const [isAddingNewHouseholdMember, setIsAddingNewHouseholdMember] =
    useState(false);

  const handleYesPress = () => {
    setIsAddingNewHouseholdMember(true);
  };

  if (!currentHouseholdMember?.householdId) {
    return <VnLoadingSpinner className="mt-2" />;
  }

  const goToHouseholdIncome = () => {
    updateHousehold(
      {
        id: currentHouseholdMember.householdId,
        householdData: {
          eligibilityLastStep: hasFinishedEligibilityFlow
            ? "FINISHED"
            : "SUPPORT_OTHER_RELATIVES",
        },
      },
      {
        onSuccess: () => {
          router.navigate("/eligibility/household-income");
        },
      },
    );
  };

  const goToSomeoneSupportsYou = () => {
    updateHousehold(
      {
        id: currentHouseholdMember.householdId,
        householdData: {
          eligibilityLastStep: hasFinishedEligibilityFlow
            ? "FINISHED"
            : "SOMEONE_SUPPORTS_YOU",
        },
      },
      {
        onSuccess: () => {
          router.navigate("/eligibility/someone-supports-you");
        },
      },
    );
  };

  const handleNoPress = () => {
    if (supporterHouseholdMemberId) {
      // If coming from supporter flow, go to household income directly
      goToHouseholdIncome();
      return;
    }

    // When pressing no, should check if at least one relationship exists different from SPOUSE and COMMON_LAW
    const hasAtLeastOneOtherRelative =
      householdMemberRelationships?.some(
        hhmRelationship =>
          !["SPOUSE", "PARTNER"].includes(hhmRelationship.relationship),
      ) ?? false;

    // If has at least one other relative, go to the next step
    if (hasAtLeastOneOtherRelative) {
      goToHouseholdIncome();
      return;
    }

    // If there is no other relation, it should ask if someone else is the supporter of the primary applicant
    goToSomeoneSupportsYou();
  };

  if (isAddingNewHouseholdMember) {
    return (
      <EligibilityAddHouseholdMemberScreen
        relationshipOptions={[
          "PARENT",
          "GRANDPARENT",
          "SIBLING",
          "CHILD",
          "STEPCHILD",
          "GRANDCHILD",
          "FOSTER_CHILD",
          "OTHER",
        ]}
        assumptions={{
          taxFilingStatus: "NON_FILER",
          maritalStatus: "NEVER_MARRIED",
          childCaregiverStatus: "NOT_CAREGIVER",
          householdMemberRelationship: {
            livingWithStatus: true,
            taxRelationship: supporterHouseholdMemberId
              ? "DEPENDENT_OF_SUPPORTER"
              : "SUPPORT_OF",
          },
          sameMailingAddress: true,
        }}
        titleI18nKey={
          supporterHouseholdMemberId
            ? "native:eligibilitySupportOtherRelativesScreen.supporterFormTitle"
            : "native:eligibilitySupportOtherRelativesScreen.formTitle"
        }
        descriptionI18nKey={
          supporterHouseholdMemberId
            ? "native:eligibilitySupportOtherRelativesScreen.supporterFormDescription"
            : "native:eligibilitySupportOtherRelativesScreen.formDescription"
        }
        titleI18nOptions={{
          supporterFirstName,
        }}
        descriptionI18nOptions={{
          supporterFirstName,
        }}
        onSuccessOrContinue={goToHouseholdIncome}
        onBackButtonPress={() => setIsAddingNewHouseholdMember(false)}
      />
    );
  }

  return (
    <EligibilityYesAndNo
      titleI18nKey={
        supporterHouseholdMemberId
          ? "native:eligibilitySupportOtherRelativesScreen.supporterTitle"
          : "native:eligibilitySupportOtherRelativesScreen.title"
      }
      descriptionI18nKey={
        supporterHouseholdMemberId
          ? "native:eligibilitySupportOtherRelativesScreen.supporterDescription"
          : "native:eligibilitySupportOtherRelativesScreen.description"
      }
      descriptionI18nOptions={{
        supporterFirstName,
      }}
      titleI18nOptions={{
        supporterFirstName,
      }}
      onNoPress={handleNoPress}
      isNoLoading={isUpdatingHousehold}
      onYesPress={handleYesPress}
    />
  );
};
